import { useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, CalendarDays, ChevronDown } from "lucide-react";
import { HERO_IMAGE, WHATSAPP_URL, AULA_EXPERIMENTAL_MSG } from "../data/siteData";

const EASE = [0.22, 1, 0.36, 1];
const waLink = `${WHATSAPP_URL}?text=${encodeURIComponent(AULA_EXPERIMENTAL_MSG)}`;

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.9, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  const specks = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        left: `${(i * 71 + 13) % 100}%`,
        top: `${(i * 37 + 9) % 70}%`,
        size: 2 + ((i * 5) % 3),
        delay: (i % 7) * 0.6,
      })),
    []
  );

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt="Campo de futebol iluminado por refletores à noite"
          className="h-[120%] w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-night/80 via-navy/80 to-night" />
        <div className="turf-lines absolute inset-0" />
      </motion.div>

      <div className="beam left-[6%]" aria-hidden="true" />
      <div className="beam right-[6%]" style={{ animationDelay: "-4.5s" }} aria-hidden="true" />

      {specks.map((s, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="absolute rounded-full bg-gold/70"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            animation: `float-y 6s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
          data-testid="hero-badge"
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-gold sm:text-xs"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          Desde 2011 em Patos de Minas/MG
        </motion.p>

        <h1 className="max-w-4xl font-display text-[15vw] uppercase leading-[0.92] tracking-wide text-white sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.25}>Treine em alto nível</MaskedLine>
          <MaskedLine delay={0.42}>
            para jogar em <span className="text-lawn">alto nível</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.8, ease: EASE }}
          data-testid="hero-subtitle"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          Escola de Futebol para crianças e jovens de 4 a 17 anos em Patos de Minas/MG
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-lawn px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy shadow-[0_0_40px_rgba(141,198,63,0.35)] transition-transform duration-200 hover:scale-[1.04]"
          >
            <MessageCircle size={18} strokeWidth={2.5} />
            Agende uma aula experimental
          </a>
          <a
            href="#categorias"
            data-testid="hero-schedule-cta"
            className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-gold/70 px-8 py-4 text-sm font-bold uppercase tracking-wide text-gold transition-colors duration-200 hover:bg-gold hover:text-navy"
          >
            <CalendarDays size={18} strokeWidth={2.5} />
            Conheça os horários
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#sobre"
        aria-label="Rolar para a próxima seção"
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 transition-colors hover:text-lawn"
      >
        <ChevronDown size={28} className="animate-bounce" />
      </motion.a>
    </section>
  );
};

export default Hero;
